import { useCallback } from "react";

/**
 * Creates a debounced version of a function that delays its execution
 * @param {Function} func - The function to debounce
 * @param {number} wait - The number of milliseconds to delay
 * @returns {Function} - The debounced function
 */
export function debounce(func, wait) {
  let timeoutId;

  const debouncedFn = function (...args) {
    const context = this;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      timeoutId = null;
      func.apply(context, args);
    }, wait);
  };

  // Add cancel method to clear the timeout
  debouncedFn.cancel = function () {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return debouncedFn;
}

/**
 * React hook for creating a debounced callback
 * @param {Function} callback - The callback to debounce
 * @param {number} delay - The delay in milliseconds
 * @returns {Function} - The debounced callback
 */
export function useDebounce(callback, delay) {
  return useCallback(
    debounce((...args) => {
      callback(...args);
    }, delay),
    [callback, delay]
  );
}
